import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { logoutApi } from '../../services/authApi';
import { SessionManager } from '../../utils/SessionManager';
import { User } from '../../types/entities';
import { toRelativePath } from '../../utils/videoImagePath';
import { UseSidebarReturn } from './types';

/**
 * Sidebarコンポーネントのロジックを管理するカスタムフック
 * 
 * @param currentUser - 現在のユーザー情報
 * @returns Sidebarコンポーネントで使用するstate, handlers, computed values
 */
export const useSidebar = (currentUser?: User): UseSidebarReturn => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [selectedMyVideoStatus, setSelectedMyVideoStatus] = useState<'public' | 'hospital_only' | 'private' | null>(null);
  const [isMyVideoSubmenuOpen, setIsMyVideoSubmenuOpen] = useState<boolean>(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  const dropdownRef = useRef<HTMLLIElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const myVideoSubmenuRef = useRef<HTMLDivElement>(null);
  
  // スワイプ用のタッチ位置を保存する状態
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  // スワイプ中のドラッグ距離を保存
  const [dragDistance, setDragDistance] = useState<number>(0);
  // スワイプ中かどうかの状態
  const [swiping, setSwiping] = useState<boolean>(false);
  // ハンドルからのドラッグかどうか
  const [isDraggingHandle, setIsDraggingHandle] = useState<boolean>(false);
  
  const showSnackbar = useSnackbar();
  
  // URLパラメータから選択状態を初期化
  useEffect(() => {
    if (location.pathname === '/my-videos') {
      const searchParams = new URLSearchParams(location.search);
      const status = searchParams.get('status');
      if (status === 'public') {
        setSelectedMyVideoStatus('public');
      } else if (status === 'hospital_only') {
        setSelectedMyVideoStatus('hospital_only');
      } else if (status === 'private') {
        setSelectedMyVideoStatus('private');
      } else {
        setSelectedMyVideoStatus(null);
      }
    } else {
      // マイビデオページ以外では選択状態をリセット
      setSelectedMyVideoStatus(null);
    }
  }, [location]);
  
  // モーダル操作
  const openModal = (): void => setIsModalOpen(true);
  const closeModal = (): void => setIsModalOpen(false);
  
  // Adminかどうかを確認
  const isAdmin = currentUser?.role === 'admin';

  // ユーザー情報から取得する値
  // icon_path は先頭スラッシュ有無が混在するため正規化して相対URLにする
  const iconUrl = currentUser?.icon_path
    ? toRelativePath(currentUser.icon_path)
    : null;
  
  // ユーザー名を取得（未設定の場合はデフォルト値）
  const userName = currentUser?.name || 'ユーザー';

  // モバイル用ドロップダウン切り替え
  const toggleDropdown = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation(); // イベントの伝播を止める
    
    // メニューを閉じる場合はアニメーションを適用
    if (isDropdownOpen && menuRef.current) {
      menuRef.current.style.animation = 'slideDownFadeOut 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards';
      
      // アニメーション完了後にステートを更新
      setTimeout(() => {
        setIsDropdownOpen(false);
        document.body.style.overflow = '';
        
        // アニメーション後にリセット
        if (menuRef.current) {
          menuRef.current.style.animation = '';
        }
      }, 300);
    } else {
      // メニューを開く場合
      setIsDropdownOpen(true);
      setDragDistance(0); // ドラッグ距離をリセット
      document.body.style.overflow = 'hidden';
    }
  };

  // タッチイベントハンドラー
  const handleTouchStart = (e: React.TouchEvent): void => {
    if (!menuRef.current) return;
    
    // タッチの開始位置がハンドル部分または上部エリアかどうかを確認
    const touchY = e.touches[0].clientY;
    const menuRect = menuRef.current.getBoundingClientRect();
    const topEdgeArea = menuRect.top + 60; // 上部エリアを広く取る (50px + 余裕分)
    
    // 上部エリアのタッチを判定
    const isTopEdgeTouch = touchY <= topEdgeArea;
    
    setIsDraggingHandle(isTopEdgeTouch);
    setTouchStartY(e.touches[0].clientY);
    
    // 上部エリア以外のタッチは通常のスクロール操作として処理
    if (!isTopEdgeTouch) return;
    
    // 上部エリアのタッチの場合
    setSwiping(true);
    if (menuRef.current) {
      menuRef.current.classList.add('swiping');
      
      // タッチ開始時にハードウェアアクセラレーションを有効化し、より滑らかな動きを実現
      menuRef.current.style.willChange = 'transform, opacity';
      
      // 触覚フィードバック (iOS/Androidで対応機種のみ)
      if (window.navigator && window.navigator.vibrate) {
        try {
          // 短い振動でタッチ開始を伝える
          window.navigator.vibrate(10);
        } catch (e) {
          // 振動APIに対応していない場合は無視
          console.log('Vibration API not supported');
        }
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent): void => {
    // 上部エリアのドラッグでない場合やタッチ開始位置がなければ何もしない
    if (!isDraggingHandle || touchStartY === null) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY;
    
    // 下方向のドラッグのみ許可（完全に開いた状態からのドラッグ）
    if (diff > 0) {
      // 最初の動きをより滑らかにするため、初期抵抗を調整
      // 0-10pxの範囲で抵抗を強め、その後徐々に通常の抵抗に移行
      let initialResistance = 1;
      if (diff < 10) {
        // 最初の10pxは抵抗を強めに設定（0.5-0.95の範囲で徐々に緩和）
        initialResistance = 0.5 + (diff / 10) * 0.45;
      }
      
      // モーションを自然に感じるよう、距離に基づいて抵抗を調整
      // 引っ張るほどわずかに抵抗が増す自然な挙動
      const resistanceFactor = Math.min(1, 1 - diff / 2000);
      const dampenedDiff = diff * (initialResistance * resistanceFactor);
      setDragDistance(dampenedDiff);
      
      // メニューのスタイルを直接操作してドラッグに追従
      if (menuRef.current) {
        // ドラッグの距離に応じて不透明度も調整（より遠くなるほど透明に）
        // 不透明度の減少を緩やかにして、長距離ドラッグでも見えるようにする
        const opacity = Math.max(1 - dampenedDiff / 800, 0.4);
        
        // 距離に応じたスケーリング効果（最初の10pxはスケーリングを抑制）
        const scaleAdjustment = diff < 10 ? (diff / 10) * 0.08 : 0.08;
        const scale = Math.max(0.92, 1 - scaleAdjustment - (diff > 10 ? (diff - 10) / 2000 : 0));
        
        // 3D効果を追加（最初の10pxは回転を抑制）
        const rotateXAdjustment = diff < 10 ? (diff / 10) * 2 : 2;
        const rotateX = Math.min(5, rotateXAdjustment + (diff > 10 ? (diff - 10) / 200 : 0));
        
        // 複合的な変形を適用（transform-originを明示的に設定）
        menuRef.current.style.transformOrigin = 'bottom center';
        menuRef.current.style.transform = `
          translateY(${dampenedDiff}px) 
          scale(${scale}) 
          rotateX(${rotateX}deg)
        `;
        
        menuRef.current.style.opacity = `${opacity}`;
        menuRef.current.classList.add('handle-dragging');
        
        // ドラッグ距離に応じてオーバーレイの不透明度も調整
        const overlay = document.querySelector('.menu-overlay');
        if (overlay instanceof HTMLElement) {
          // 最初の10pxではオーバーレイの不透明度変化を抑制
          const overlayOpacity = diff < 10 
            ? 1 - (diff / 10) * 0.1 
            : Math.max(0.9 - (diff - 10) / 1000, 0.1);
          
          overlay.style.opacity = `${overlayOpacity}`;
          overlay.style.transition = 'none'; // トランジションを無効化して即時反映
        }
        
        // 触覚フィードバック
        if (window.navigator && window.navigator.vibrate) {
          try {
            const thresholds = [200, 400, 600, 800];
            const prevDistance = dragDistance;
            
            for (const threshold of thresholds) {
              if (prevDistance < threshold && dampenedDiff >= threshold) {
                window.navigator.vibrate(15);
                break;
              }
            }
          } catch (e) {
            // 振動APIに対応していない場合は無視
          }
        }
      }
      
      // コンテンツエリアのスクロールを防止
      e.preventDefault();
    }
  };

  const handleTouchEnd = (): void => {
    // タッチイベント終了時、上部エリアをドラッグしていた場合のみ処理
    if (!isDraggingHandle) {
      setIsDraggingHandle(false);
      return;
    }
    
    setSwiping(false);
    if (menuRef.current) {
      menuRef.current.classList.remove('swiping');
      menuRef.current.style.willChange = ''; // ハードウェアアクセラレーションをリセット
    }
    
    // 触覚フィードバック
    if (window.navigator && window.navigator.vibrate) {
      try {
        window.navigator.vibrate(10);
      } catch (e) {
        // 振動APIに対応していない場合は無視
      }
    }
    
    // ドラッグ距離が10px以上ある場合はメニューを閉じる（閾値を調整）
    if (dragDistance > 10 && menuRef.current) {
      // オーバーレイもフェードアウト
      const overlay = document.querySelector('.menu-overlay');
      if (overlay instanceof HTMLElement) {
        overlay.style.animation = 'fadeOut 0.3s ease forwards';
      }
      
      // ドラッグした状態から自然に閉じるアニメーション
      const currentY = dragDistance;
      const currentScale = Math.max(0.92, 1 - currentY / 2000);
      const currentOpacity = Math.max(1 - currentY / 800, 0.4);
      const currentRotateX = Math.min(5, currentY / 200); // 現在の回転角度
      
      // より滑らかで自然なアニメーションのためのカスタムキーフレーム
      const slideDownCustomStyle = document.createElement('style');
      slideDownCustomStyle.innerHTML = `
        @keyframes slideDownCustom {
          from {
            transform: translateY(${currentY}px) scale(${currentScale}) rotateX(${currentRotateX}deg);
            opacity: ${currentOpacity};
          }
          to {
            transform: translateY(${window.innerHeight}px) scale(0.9) rotateX(10deg);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(slideDownCustomStyle);
      
      // スワイプが早いほど、アニメーションも速くする
      // ドラッグ距離が大きいほどアニメーションが速くなる
      const animationDuration = Math.max(0.25, 0.4 - (currentY / 2000));
      
      menuRef.current.style.animation = `slideDownCustom ${animationDuration}s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`;
      
      // アニメーション終了後にドロップダウンを閉じる
      setTimeout(() => {
        setIsDropdownOpen(false);
        document.body.style.overflow = '';
        
        // スタイルをリセット
        if (menuRef.current) {
          menuRef.current.style.transition = '';
          menuRef.current.style.transform = '';
          menuRef.current.style.opacity = '';
          menuRef.current.style.animation = '';
          menuRef.current.style.scale = '';
          menuRef.current.classList.remove('handle-dragging');
        }
        
        // カスタムアニメーションスタイルを削除
        if (document.head.contains(slideDownCustomStyle)) {
          document.head.removeChild(slideDownCustomStyle);
        }
        
        // オーバーレイもリセット
        if (overlay instanceof HTMLElement) {
          overlay.style.opacity = '';
          overlay.style.transition = '';
        }
      }, animationDuration * 1000); // アニメーション時間に合わせる
    } 
    // 10px未満の場合は元の位置に戻す（閾値を調整）
    else if (dragDistance > 0 && menuRef.current) {
      // スプリングバックの強さはドラッグ距離に応じて調整
      const springiness = Math.min(0.8, 0.3 + (dragDistance / 1000));
      
      // より自然なスプリングバックアニメーション
      menuRef.current.style.transition = `transform 0.4s cubic-bezier(0.34, ${1.5 + springiness}, 0.64, 1), opacity 0.3s ease`;
      menuRef.current.style.transform = '';
      menuRef.current.style.opacity = '1';
      menuRef.current.classList.remove('handle-dragging');
      
      // オーバーレイも元に戻す
      const overlay = document.querySelector('.menu-overlay');
      if (overlay instanceof HTMLElement) {
        overlay.style.transition = 'opacity 0.3s ease';
        overlay.style.opacity = '1';
      }
      
      // トランジション終了後にスタイルをリセット
      setTimeout(() => {
        if (menuRef.current) {
          menuRef.current.style.transition = '';
        }
        
        if (overlay instanceof HTMLElement) {
          overlay.style.transition = '';
        }
      }, 400);
    }
    
    setIsDraggingHandle(false);
    setTouchStartY(null);
    setDragDistance(0);
  };
  
  const handleAccount = (): void => {
    setIsDropdownOpen(false);
    document.body.style.overflow = '';
    navigate('/account');
  };
  
  const handleLogout = async (): Promise<void> => {
    try {
      // まずローカルセッションをクリア（UIの即時反映のため）
      SessionManager.logout();
      
      // UIの更新（DropdownOpenの状態をリセット）
      setIsDropdownOpen(false);
      document.body.style.overflow = '';
      
      // バックエンドへのログアウトリクエスト（非同期で実行）
      await logoutApi().catch(err => {
        console.error('ログアウト処理エラー:', err);
      });
    } catch (error) {
      console.error('ログアウト処理エラー:', error);
    }
  };
  
  // オーバーレイのクリックハンドラーを独立した関数として定義
  const handleOverlayClick = (): void => {
    // オーバーレイ自体もフェードアウト
    const overlay = document.querySelector('.menu-overlay');
    if (overlay instanceof HTMLElement) {
      overlay.style.animation = 'fadeOut 0.3s ease forwards';
    }
    
    // メニューを閉じる際もアニメーションを適用
    if (menuRef.current) {
      menuRef.current.style.animation = 'slideDownFadeOut 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards';
      
      // アニメーション完了後に状態を更新
      setTimeout(() => {
        setIsDropdownOpen(false);
        document.body.style.overflow = '';
        
        // アニメーション後にリセット
        if (menuRef.current) {
          menuRef.current.style.animation = '';
        }
      }, 300);
    } else {
      setIsDropdownOpen(false);
      document.body.style.overflow = '';
    }
  };

  // Adminユーザーがユーザーリストページへのナビゲーションを処理する関数
  const handleUserManagement = (): void => {
    navigate('/admin/users');
    setIsDropdownOpen(false);
  };

  // モバイル用のAdmin用ユーザ管理ナビゲーション関数
  const handleUserManagementMobile = (): void => {
    navigate('/admin/users');
    setIsDropdownOpen(false);
  };

  const handleAdminNotifications = (): void => {
    navigate('/admin/notifications');
    setIsDropdownOpen(false);
  };

  const handleAdminNotificationsMobile = (): void => {
    navigate('/admin/notifications');
    setIsDropdownOpen(false);
  };

  // モバイル版マイビデオサブメニューを閉じる関数
  const closeMyVideoSubmenu = (): void => {
    setIsMyVideoSubmenuOpen(false);
  };

  // マイビデオの選択状態を切り替え（null → public → hospital_only → private → public の循環）し、URLに遷移
  // モバイル版ではサブメニューの表示/非表示を切り替える
  const toggleMyVideoDropdown = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    
    // モバイル版の場合、サブメニューの表示/非表示を切り替え
    const isMobile = window.innerWidth <= 650;
    if (isMobile) {
      setIsMyVideoSubmenuOpen(prev => !prev);
    } else {
      // PC版の場合、従来通り選択状態を切り替えてURLに遷移
      setSelectedMyVideoStatus(prev => {
        let nextStatus: 'public' | 'hospital_only' | 'private';
        if (prev === null) {
          nextStatus = 'public';
        } else if (prev === 'public') {
          nextStatus = 'hospital_only';
        } else if (prev === 'hospital_only') {
          nextStatus = 'private';
        } else {
          nextStatus = 'public';
        }
        // 選択状態に応じてURLに遷移
        navigate(`/my-videos?status=${nextStatus}`);
        return nextStatus;
      });
    }
  };

  // モバイル用ドロップダウン外部クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) && isDropdownOpen) {
        // メニューを閉じる際もアニメーションを適用
        if (menuRef.current) {
          menuRef.current.style.animation = 'slideDownFadeOut 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards';
          
          // アニメーション完了後に状態を更新
          setTimeout(() => {
            setIsDropdownOpen(false);
            document.body.style.overflow = '';
            
            // アニメーション後にリセット
            if (menuRef.current) {
              menuRef.current.style.animation = '';
            }
          }, 300);
        } else {
          setIsDropdownOpen(false);
          document.body.style.overflow = '';
        }
      }
    };
    
    const handleEscapeKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && isDropdownOpen) {
        // メニューを閉じる際もアニメーションを適用
        if (menuRef.current) {
          menuRef.current.style.animation = 'slideDownFadeOut 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards';
          
          // アニメーション完了後に状態を更新
          setTimeout(() => {
            setIsDropdownOpen(false);
            document.body.style.overflow = '';
            
            // アニメーション後にリセット
            if (menuRef.current) {
              menuRef.current.style.animation = '';
            }
          }, 300);
        } else {
          setIsDropdownOpen(false);
          document.body.style.overflow = '';
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      if (isDropdownOpen) {
        document.body.style.overflow = '';
      }
    };
  }, [isDropdownOpen]);

  // ブラウザバック制御のためのイベント設定
  useEffect(() => {
    const handlePopState = (): boolean => {
      // メニューが開いている状態でブラウザバックが押された場合
      if (isDropdownOpen) {
        // popstateイベントでpreventDefaultは動作しないので、別の方法で処理する
        
        // メニューを閉じる処理を実行
        if (menuRef.current) {
          menuRef.current.style.animation = 'slideDownFadeOut 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards';
          
          // オーバーレイもフェードアウト
          const overlay = document.querySelector('.menu-overlay');
          if (overlay instanceof HTMLElement) {
            overlay.style.animation = 'fadeOut 0.3s ease forwards';
          }
          
          // アニメーション完了後に状態を更新
          setTimeout(() => {
            setIsDropdownOpen(false);
            document.body.style.overflow = '';
            
            // アニメーション後にリセット
            if (menuRef.current) {
              menuRef.current.style.animation = '';
            }
          }, 300);
        } else {
          setIsDropdownOpen(false);
          document.body.style.overflow = '';
        }
        
        // 履歴を元に戻さないようにするため、現在の状態を維持する新しいエントリを追加
        window.history.pushState(null, '', window.location.pathname);
        
        // イベントをここで処理したことを示す
        return true;
      }
      return false;
    };
    
    // メニューが開いたときだけ履歴エントリを追加
    if (isDropdownOpen) {
      // メニューを開いたときに履歴エントリを追加
      window.history.pushState(null, '', window.location.pathname);
    }
    
    // popstateイベントリスナーを設定
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDropdownOpen]);

  // モバイル用ドロップダウンメニューのDOMマウント時にハンドル要素の参照を取得
  useEffect(() => {
    if (isDropdownOpen && menuRef.current) {
      // ハンドルにタッチエリアを確保するためのスタイルを追加
      
      // ::before擬似要素にはJSで直接アクセスできないため、
      // メニュー自体にイベントを設定し、位置で判定する方式を採用
      
      // 疑似要素をドラッグしているように見せるためのスタイル調整
      const handleDragStyle = document.createElement('style');
      handleDragStyle.innerHTML = `
        .dropdown-menu-mobile.handle-dragging::before {
          background-color: #aaaaaa;
          width: 50px;
          transform: scaleY(1.2);
        }
      `;
      document.head.appendChild(handleDragStyle);
      
      return () => {
        document.head.removeChild(handleDragStyle);
      };
    }
    return undefined;
  }, [isDropdownOpen]);

  // モバイル版マイビデオサブメニューの外部タップ検知
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent): void => {
      if (
        isMyVideoSubmenuOpen &&
        myVideoSubmenuRef.current &&
        !myVideoSubmenuRef.current.contains(event.target as Node)
      ) {
        closeMyVideoSubmenu();
      }
    };

    if (isMyVideoSubmenuOpen) {
      // タッチイベントとマウスイベントの両方に対応
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMyVideoSubmenuOpen]);

  return {
    isModalOpen,
    isDropdownOpen,
    openModal,
    closeModal,
    toggleDropdown,
    handleAccount,
    handleLogout,
    handleOverlayClick,
    handleUserManagement,
    handleUserManagementMobile,
    handleAdminNotifications,
    handleAdminNotificationsMobile,
    isAdmin,
    iconUrl,
    userName,
    dropdownRef,
    menuRef,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    swiping,
    isDraggingHandle,
    selectedMyVideoStatus,
    toggleMyVideoDropdown,
    isMyVideoSubmenuOpen,
    myVideoSubmenuRef,
    closeMyVideoSubmenu
  } as UseSidebarReturn;
};

export default useSidebar; 
